/**
 * Pure type filter config registry — no icon or React imports.
 * Extracted from individual 3P config files for bundle isolation.
 *
 * This is the source of truth for ProductKeys3P → TypeFilterConfig3P
 * mappings used by getTypeMapping() in constants.ts.
 *
 * Dynamic entries (feature-flag-gated) are expressed as getter functions.
 * Static entries are plain Map values.
 */

import { fg } from '@atlaskit/platform-feature-flags';

import { serveFederatedSlackExperiment } from '../../utils/experiments/experiment-utils';
import {
	DEFAULT_CLOUD_GRAPH_CALENDAR_EVENT_ENTITY_ATI,
	DEFAULT_CLOUD_GRAPH_COMMENT_ENTITY_ATI,
	DEFAULT_CLOUD_GRAPH_CONVERSATION_ENTITY_ATI,
	DEFAULT_CLOUD_GRAPH_CUSTOMER_ORG_ENTITY_ATI,
	DEFAULT_CLOUD_GRAPH_DASHBOARD_ENTITY_ATI,
	DEFAULT_CLOUD_GRAPH_DATA_TABLE_ENTITY_ATI,
	DEFAULT_CLOUD_GRAPH_DEAL_ENTITY_ATI,
	DEFAULT_CLOUD_GRAPH_DESIGN_ENTITY_ATI,
	DEFAULT_CLOUD_GRAPH_DEVINFO_BRANCH_ENTITY_ATI,
	DEFAULT_CLOUD_GRAPH_DEVINFO_COMMIT_ENTITY_ATI,
	DEFAULT_CLOUD_GRAPH_DEVINFO_PULL_REQUEST_ENTITY_ATI,
	DEFAULT_CLOUD_GRAPH_DEVINFO_REPOSITORY_ENTITY_ATI,
	DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
	DEFAULT_CLOUD_GRAPH_MESSAGE_ENTITY_ATI,
	DEFAULT_CLOUD_GRAPH_PROJECT_ENTITY_ATI,
	DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
	DEFAULT_CLOUD_GRAPH_SPACE_ENTITY_ATI,
	DEFAULT_CLOUD_GRAPH_VIDEO_ENTITY_ATI,
	DEFAULT_CLOUD_GRAPH_WORK_ITEM_ENTITY_ATI,
	FEDERATED_GOOGLE_EMAIL_ENTITY_ATI,
	FEDERATED_MICROSOFT_EMAIL_ENTITY_ATI,
} from '../entities';
import {
	NounSubtypeKey,
	type TypeFilterConfig3P,
	type TypeFilterValue3PEntities,
	TypeFilterValueKey,
} from '../filters/universal-type';

import { ProductKeys3P } from './product-keys-3p';
import { buildSalesforce3PTypeFilterConfig } from './salesforce/type-filter-config';

// --- Dynamic type filter config builders (feature-flag-gated) ---

const getGoogleDriveTypeFilterConfig = () => {
	const config = new Map<TypeFilterValueKey, TypeFilterValue3PEntities>();

	config.set(TypeFilterValueKey.Document, {
		entities3P: [
			...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
			...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
		],
		subTypes3P: fg('rovo_fps_enable_google_drive_new_types_filter')
			? [NounSubtypeKey.Document, NounSubtypeKey.Pdf, NounSubtypeKey.Page, NounSubtypeKey.Blogpost]
			: [NounSubtypeKey.Document, NounSubtypeKey.Pdf],
	});

	config.set(TypeFilterValueKey.Spreadsheet, {
		entities3P: [
			...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
			...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
		],
		subTypes3P: [NounSubtypeKey.Spreadsheet],
	});

	config.set(TypeFilterValueKey.Presentation, {
		entities3P: [
			...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
			...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
		],
		subTypes3P: [NounSubtypeKey.Presentation],
	});

	if (fg('rovo_fps_enable_video_noun_filter')) {
		config.set(TypeFilterValueKey.Video, {
			entities3P: [...DEFAULT_CLOUD_GRAPH_VIDEO_ENTITY_ATI],
			subTypes3P: [NounSubtypeKey.Video],
		});
	}

	if (fg('rovo_fps_enable_google_drive_space_type_filter')) {
		config.set(TypeFilterValueKey.Space, {
			entities3P: [...DEFAULT_CLOUD_GRAPH_SPACE_ENTITY_ATI],
			subTypes3P: [NounSubtypeKey.Space],
		});
	}

	if (fg('rovo_fps_enable_google_drive_new_types_filter')) {
		config.set(TypeFilterValueKey.Image, {
			entities3P: [
				...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
				...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
			],
			subTypes3P: [NounSubtypeKey.Image],
		});
		config.set(TypeFilterValueKey.Folder, {
			entities3P: [
				...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
				...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
			],
			subTypes3P: [NounSubtypeKey.Folder],
		});
		config.set(TypeFilterValueKey.Audio, {
			entities3P: [
				...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
				...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
			],
			subTypes3P: [NounSubtypeKey.Audio],
		});
		config.set(TypeFilterValueKey.Pdf, {
			entities3P: [
				...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
				...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
			],
			subTypes3P: [NounSubtypeKey.Pdf],
		});
		config.set(TypeFilterValueKey.Code, {
			entities3P: [
				...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
				...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
			],
			subTypes3P: [NounSubtypeKey.Code],
		});
		config.set(TypeFilterValueKey.Form, {
			entities3P: [
				...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
				...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
			],
			subTypes3P: [NounSubtypeKey.Form],
		});
		config.set(TypeFilterValueKey.Notebook, {
			entities3P: [
				...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
				...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
			],
			subTypes3P: [NounSubtypeKey.Notebook],
		});

		config.set(TypeFilterValueKey.WebPage, {
			entities3P: [
				...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
				...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
			],
			subTypes3P: [NounSubtypeKey.WebPage, NounSubtypeKey.Shortcut],
		});
		config.set(TypeFilterValueKey.Other, {
			entities3P: [
				...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
				...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
			],
			subTypes3P: [NounSubtypeKey.Other, NounSubtypeKey.Archive],
		});
	}

	// Comments are embedded within documents as linkedEntities, not standalone entities.
	// When this filter is selected, the search will request documents with linkedEntities: ["ati:cloud:graph:comment"]
	// We add this config (with entities3P) so the Comment filter appears in the UI; the filter logic skips adding
	// Comment to productEntities and uses linkedEntities instead, so results remain documents.
	if (fg('enable_3p_embedded_comments_search')) {
		config.set(TypeFilterValueKey.Comment, {
			entities3P: DEFAULT_CLOUD_GRAPH_COMMENT_ENTITY_ATI,
			subTypes3P: [],
		});
	}

	return config;
};

const getSharePointTypeFilterConfig = () => {
	const config = new Map<TypeFilterValueKey, TypeFilterValue3PEntities>();

	config.set(TypeFilterValueKey.Document, {
		entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
		subTypes3P: fg('rovo_fps_enable_sharepoint_new_types_filter')
			? [NounSubtypeKey.Document, NounSubtypeKey.Pdf, NounSubtypeKey.Page, NounSubtypeKey.Blogpost]
			: [NounSubtypeKey.Document, NounSubtypeKey.Pdf],
	});

	config.set(TypeFilterValueKey.Spreadsheet, {
		entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
		subTypes3P: [NounSubtypeKey.Spreadsheet],
	});

	config.set(TypeFilterValueKey.Presentation, {
		entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
		subTypes3P: [NounSubtypeKey.Presentation],
	});

	if (fg('rovo_fps_sharepoint_enable_video_noun_filter')) {
		config.set(TypeFilterValueKey.Video, {
			entities3P: DEFAULT_CLOUD_GRAPH_VIDEO_ENTITY_ATI,
			subTypes3P: [NounSubtypeKey.Video],
		});
	}

	if (fg('rovo_fps_enable_sharepoint_new_types_filter')) {
		config.set(TypeFilterValueKey.Image, {
			entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
			subTypes3P: [NounSubtypeKey.Image],
		});
		config.set(TypeFilterValueKey.Folder, {
			entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
			subTypes3P: [NounSubtypeKey.Folder],
		});
		// Document -> video fallback (only when video noun filter is OFF)
		if (!fg('rovo_fps_sharepoint_enable_video_noun_filter')) {
			config.set(TypeFilterValueKey.Video, {
				entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
				subTypes3P: [NounSubtypeKey.Video],
			});
		}
		config.set(TypeFilterValueKey.Pdf, {
			entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
			subTypes3P: [NounSubtypeKey.Pdf],
		});
		config.set(TypeFilterValueKey.Notebook, {
			entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
			subTypes3P: [NounSubtypeKey.Notebook],
		});

		config.set(TypeFilterValueKey.WebPage, {
			entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
			subTypes3P: [NounSubtypeKey.WebPage, NounSubtypeKey.Shortcut],
		});
		config.set(TypeFilterValueKey.Other, {
			entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
			subTypes3P: [NounSubtypeKey.Other, NounSubtypeKey.Archive],
		});
	}

	// Comments are embedded within documents as linkedEntities, not standalone entities.
	// When this filter is selected, the search will request documents with linkedEntities: ["ati:cloud:graph:comment"]
	// We add this config (with entities3P) so the Comment filter appears in the UI; the filter logic skips adding
	// Comment to productEntities and uses linkedEntities instead, so results remain documents.
	if (fg('enable_3p_embedded_comments_search')) {
		config.set(TypeFilterValueKey.Comment, {
			entities3P: DEFAULT_CLOUD_GRAPH_COMMENT_ENTITY_ATI,
			subTypes3P: [],
		});
	}

	return config;
};

const getServiceNowTypeFilterConfig = () => {
	const config = new Map<TypeFilterValueKey, TypeFilterValue3PEntities>();

	if (fg('rovo_fps_servicenow_type_filter_subtypes')) {
		config.set(TypeFilterValueKey.Incident, {
			entities3P: DEFAULT_CLOUD_GRAPH_WORK_ITEM_ENTITY_ATI,
			subTypes3P: [NounSubtypeKey.Incident],
		});
		config.set(TypeFilterValueKey.CatalogItem, {
			entities3P: DEFAULT_CLOUD_GRAPH_WORK_ITEM_ENTITY_ATI,
			subTypes3P: [NounSubtypeKey.CatalogItem],
		});
		config.set(TypeFilterValueKey.RequestedItem, {
			entities3P: DEFAULT_CLOUD_GRAPH_WORK_ITEM_ENTITY_ATI,
			subTypes3P: [NounSubtypeKey.RequestedItem],
		});
		config.set(TypeFilterValueKey.KnowledgeArticle, {
			entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
			subTypes3P: [NounSubtypeKey.KnowledgeArticle],
		});
	} else {
		config.set(TypeFilterValueKey.WorkItem, {
			entities3P: DEFAULT_CLOUD_GRAPH_WORK_ITEM_ENTITY_ATI,
			subTypes3P: [],
		});
	}

	if (fg('rovo_search_document_filter_enable_for_servicenow')) {
		config.set(TypeFilterValueKey.Document, {
			entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
			subTypes3P: [NounSubtypeKey.Document, NounSubtypeKey.Article],
		});
	}

	if (fg('enable_rovo_search_comment_filter_for_servicenow')) {
		config.set(TypeFilterValueKey.Comment, {
			entities3P: DEFAULT_CLOUD_GRAPH_COMMENT_ENTITY_ATI,
			subTypes3P: [],
		});
	}

	return config;
};

const getSlackTypeFilterConfig = () => {
	const typeFilter = new Map<TypeFilterValueKey, TypeFilterValue3PEntities>();

	// Always include Message
	typeFilter.set(TypeFilterValueKey.Message, {
		entities3P: DEFAULT_CLOUD_GRAPH_MESSAGE_ENTITY_ATI,
		subTypes3P: [],
	});

	// Include Conversation (when federated search is disabled OR slack channel search is enabled)
	if (!serveFederatedSlackExperiment() || fg('ai_3p_slack_channel_search_enabled')) {
		typeFilter.set(TypeFilterValueKey.Conversation, {
			entities3P: DEFAULT_CLOUD_GRAPH_CONVERSATION_ENTITY_ATI,
			subTypes3P: [],
		});
	}

	return typeFilter;
};

const getTeamsTypeFilterConfig = () => {
	const config = new Map<TypeFilterValueKey, TypeFilterValue3PEntities>();

	config.set(TypeFilterValueKey.Conversation, {
		entities3P: [
			...DEFAULT_CLOUD_GRAPH_CONVERSATION_ENTITY_ATI,
			...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
		],
		subTypes3P: [],
	});

	config.set(TypeFilterValueKey.Message, {
		entities3P: DEFAULT_CLOUD_GRAPH_MESSAGE_ENTITY_ATI,
		subTypes3P: [],
	});

	if (fg('rovo_fps_enable_ms_teams_meeting_type_filter')) {
		config.set(TypeFilterValueKey.Video, {
			entities3P: DEFAULT_CLOUD_GRAPH_VIDEO_ENTITY_ATI,
			subTypes3P: [],
		});
	}

	if (fg('rovo_fps_enable_ms_teams_attachment_type_filter')) {
		config.set(TypeFilterValueKey.Attachment, {
			entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
			subTypes3P: [],
		});
	}

	return config;
};

const getZendeskTypeFilterConfig = () => {
	const config = new Map<TypeFilterValueKey, TypeFilterValue3PEntities>();

	if (fg('rovo_search_zendesk_new_filter')) {
		config.set(TypeFilterValueKey.Ticket, {
			entities3P: DEFAULT_CLOUD_GRAPH_WORK_ITEM_ENTITY_ATI,
			subTypes3P: [],
		});

		config.set(TypeFilterValueKey.Post, {
			entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
			subTypes3P: [NounSubtypeKey.Post],
		});

		config.set(TypeFilterValueKey.Article, {
			entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
			subTypes3P: [NounSubtypeKey.Article],
		});

		config.set(TypeFilterValueKey.Comment, {
			entities3P: DEFAULT_CLOUD_GRAPH_COMMENT_ENTITY_ATI,
			subTypes3P: [],
		});
	} else {
		config.set(TypeFilterValueKey.WorkItem, {
			entities3P: DEFAULT_CLOUD_GRAPH_WORK_ITEM_ENTITY_ATI,
			subTypes3P: [],
		});

		config.set(TypeFilterValueKey.Document, {
			entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
			subTypes3P: [NounSubtypeKey.Document, NounSubtypeKey.Article],
		});
	}

	return config;
};

// --- Static + dynamic type filter config registry ---

/**
 * Pure registry of ProductKeys3P → TypeFilterConfig3P.
 * Each entry is either a static Map or a getter function for dynamic configs.
 * Used by getTypeMapping() in constants.ts as a drop-in replacement
 * for iterating ThirdPartyConfigs.
 */
type TypeFilterConfigEntry = TypeFilterConfig3P | (() => TypeFilterConfig3P);

export const typeFilterConfigRegistry = new Map<ProductKeys3P, TypeFilterConfigEntry>([
	[
		ProductKeys3P.AtlassianWebCrawler,
		new Map([
			[
				TypeFilterValueKey.WebPage,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.Figma,
		new Map([
			[
				TypeFilterValueKey.Design,
				{
					entities3P: [
						...DEFAULT_CLOUD_GRAPH_DESIGN_ENTITY_ATI,
						...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
					],
					subTypes3P: [NounSubtypeKey.Design],
				},
			],
		]),
	],
	[
		ProductKeys3P.Github,
		new Map([
			[
				TypeFilterValueKey.Repository,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_REPOSITORY_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.PullRequest,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_PULL_REQUEST_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Branch,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_BRANCH_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Commit,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_COMMIT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[ProductKeys3P.GoogleDrive, getGoogleDriveTypeFilterConfig],
	[ProductKeys3P.Sharepoint, getSharePointTypeFilterConfig],
	[
		ProductKeys3P.Loom,
		new Map([
			[
				TypeFilterValueKey.Video,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_VIDEO_ENTITY_ATI,
					subTypes3P: [NounSubtypeKey.Video],
				},
			],
		]),
	],
	[
		ProductKeys3P.OneDrive,
		new Map([
			[
				TypeFilterValueKey.Document,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					subTypes3P: [NounSubtypeKey.Document, NounSubtypeKey.Pdf],
				},
			],
			[
				TypeFilterValueKey.Spreadsheet,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					subTypes3P: [NounSubtypeKey.Spreadsheet],
				},
			],
			[
				TypeFilterValueKey.Presentation,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					subTypes3P: [NounSubtypeKey.Presentation],
				},
			],
		]),
	],
	[
		ProductKeys3P.Dropbox,
		new Map([
			[
				TypeFilterValueKey.Document,
				{
					entities3P: [
						...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
						...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
					],
					subTypes3P: [NounSubtypeKey.Document, NounSubtypeKey.Pdf],
				},
			],
			[
				TypeFilterValueKey.Spreadsheet,
				{
					entities3P: [
						...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
						...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
					],
					subTypes3P: [NounSubtypeKey.Spreadsheet],
				},
			],
			[
				TypeFilterValueKey.Presentation,
				{
					entities3P: [
						...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
						...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
					],
					subTypes3P: [NounSubtypeKey.Presentation],
				},
			],
		]),
	],
	[ProductKeys3P.Slack, getSlackTypeFilterConfig],
	[ProductKeys3P.Teams, getTeamsTypeFilterConfig],
	[
		ProductKeys3P.GoogleCalendar,
		new Map([
			[
				TypeFilterValueKey.Calendar,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CALENDAR_EVENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.Gmail,
		new Map([
			[
				TypeFilterValueKey.Message,
				{
					entities3P: [FEDERATED_GOOGLE_EMAIL_ENTITY_ATI],
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.OutlookCalendar,
		new Map([
			[
				TypeFilterValueKey.Calendar,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CALENDAR_EVENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.OutlookMail,
		new Map([
			[
				TypeFilterValueKey.Message,
				{
					entities3P: [FEDERATED_MICROSOFT_EMAIL_ENTITY_ATI],
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.Asana,
		new Map([
			[
				TypeFilterValueKey.WorkItem,
				{
					entities3P: [
						...DEFAULT_CLOUD_GRAPH_WORK_ITEM_ENTITY_ATI,
						...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
					],
					subTypes3P: [NounSubtypeKey.Task],
				},
			],
		]),
	],
	[ProductKeys3P.Zendesk, getZendeskTypeFilterConfig],
	[
		ProductKeys3P.Airtable,
		new Map([
			[
				TypeFilterValueKey.Document,
				{
					entities3P: [
						...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
						...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
					],
					subTypes3P: [NounSubtypeKey.Document],
				},
			],
		]),
	],
	[
		ProductKeys3P.Smartsheet,
		new Map([
			[
				TypeFilterValueKey.Document,
				{
					entities3P: [
						...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
						...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
					],
					subTypes3P: [NounSubtypeKey.Document],
				},
			],
		]),
	],
	[
		ProductKeys3P.Notion,
		new Map([
			[
				TypeFilterValueKey.Document,
				{
					entities3P: [
						...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
						...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
					],
					subTypes3P: [NounSubtypeKey.Document],
				},
			],
		]),
	],
	[
		ProductKeys3P.Miro,
		new Map([
			[
				TypeFilterValueKey.Design,
				{
					entities3P: [
						...DEFAULT_CLOUD_GRAPH_DESIGN_ENTITY_ATI,
						...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
					],
					subTypes3P: [NounSubtypeKey.Design],
				},
			],
		]),
	],
	[
		ProductKeys3P.Docusign,
		new Map([
			[
				TypeFilterValueKey.Document,
				{
					entities3P: [
						...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
						...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
					],
					subTypes3P: [NounSubtypeKey.Document],
				},
			],
		]),
	],
	[
		ProductKeys3P.Lucid,
		new Map([
			[
				TypeFilterValueKey.Design,
				{
					entities3P: [
						...DEFAULT_CLOUD_GRAPH_DESIGN_ENTITY_ATI,
						...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
					],
					subTypes3P: [NounSubtypeKey.Design],
				},
			],
		]),
	],
	[
		ProductKeys3P.Monday,
		new Map([
			[
				TypeFilterValueKey.Document,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.AzureDevops,
		new Map([
			[
				TypeFilterValueKey.Repository,
				{
					entities3P: [
						...DEFAULT_CLOUD_GRAPH_DEVINFO_REPOSITORY_ENTITY_ATI,
						...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
					],
					subTypes3P: [NounSubtypeKey.Repository],
				},
			],
		]),
	],
	[
		ProductKeys3P.Gitlab,
		new Map([
			[
				TypeFilterValueKey.Repository,
				{
					entities3P: [
						...DEFAULT_CLOUD_GRAPH_DEVINFO_REPOSITORY_ENTITY_ATI,
						...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
					],
					subTypes3P: [NounSubtypeKey.Repository, NounSubtypeKey.Project],
				},
			],
		]),
	],
	[
		ProductKeys3P.ConfluenceDC,
		new Map([
			[
				TypeFilterValueKey.Space,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_SPACE_ENTITY_ATI,
					subTypes3P: [NounSubtypeKey.Space],
				},
			],
			[
				TypeFilterValueKey.Page,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					subTypes3P: [NounSubtypeKey.Page],
				},
			],
			[
				TypeFilterValueKey.Blogpost,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					subTypes3P: [NounSubtypeKey.Blogpost],
				},
			],
			[
				TypeFilterValueKey.Comment,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_COMMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Attachment,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					subTypes3P: [
						NounSubtypeKey.Archive,
						NounSubtypeKey.Audio,
						NounSubtypeKey.Code,
						NounSubtypeKey.Document,
						NounSubtypeKey.Image,
						NounSubtypeKey.Pdf,
						NounSubtypeKey.Presentation,
						NounSubtypeKey.Spreadsheet,
						NounSubtypeKey.Video,
						NounSubtypeKey.WebPage,
						NounSubtypeKey.Other,
					],
				},
			],
		]),
	],
	[
		ProductKeys3P.Canva,
		new Map([
			[
				TypeFilterValueKey.Design,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
					subTypes3P: [NounSubtypeKey.Design],
				},
			],
		]),
	],
	[
		ProductKeys3P.Box,
		new Map([
			[
				TypeFilterValueKey.Document,
				{
					entities3P: [
						...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
						...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
					],
					subTypes3P: [NounSubtypeKey.Document, NounSubtypeKey.Pdf],
				},
			],
			[
				TypeFilterValueKey.Spreadsheet,
				{
					entities3P: [
						...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
						...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
					],
					subTypes3P: [NounSubtypeKey.Spreadsheet],
				},
			],
			[
				TypeFilterValueKey.Presentation,
				{
					entities3P: [
						...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
						...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
					],
					subTypes3P: [NounSubtypeKey.Presentation],
				},
			],
		]),
	],
	[
		ProductKeys3P.Zeplin,
		new Map([
			[
				TypeFilterValueKey.Design,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
					subTypes3P: [NounSubtypeKey.Design],
				},
			],
		]),
	],
	[ProductKeys3P.ServiceNow, getServiceNowTypeFilterConfig],
	[
		ProductKeys3P.Freshservice,
		new Map([
			[
				TypeFilterValueKey.WorkItem,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_WORK_ITEM_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.PowerBI,
		new Map([
			[
				TypeFilterValueKey.Dashboard,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
					subTypes3P: [NounSubtypeKey.Dashboard],
				},
			],
		]),
	],
	[
		ProductKeys3P.ClickUp,
		new Map([
			[
				TypeFilterValueKey.WorkItem,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.Amplitude,
		new Map([
			[
				TypeFilterValueKey.Dashboard,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
					subTypes3P: [NounSubtypeKey.Dashboard],
				},
			],
		]),
	],
	[
		ProductKeys3P.Stripe,
		new Map([
			[
				TypeFilterValueKey.Invoice,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
					subTypes3P: [NounSubtypeKey.Invoice],
				},
			],
		]),
	],
	[
		ProductKeys3P.AdobeXD,
		new Map([
			[
				TypeFilterValueKey.Design,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
					subTypes3P: [NounSubtypeKey.Design],
				},
			],
		]),
	],
	[
		ProductKeys3P.Aha,
		new Map([
			[
				TypeFilterValueKey.WorkItem,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_WORK_ITEM_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.AdobeSign,
		new Map([
			[
				TypeFilterValueKey.Document,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.Mural,
		new Map([
			[
				TypeFilterValueKey.Design,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
					subTypes3P: [NounSubtypeKey.Design],
				},
			],
		]),
	],
	[
		ProductKeys3P.Webex,
		new Map([
			[
				TypeFilterValueKey.Video,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
					subTypes3P: [NounSubtypeKey.Video],
				},
			],
		]),
	],
	[
		ProductKeys3P.Todoist,
		new Map([
			[
				TypeFilterValueKey.WorkItem,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.PagerDuty,
		new Map([
			[
				TypeFilterValueKey.WorkItem,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.Pipedrive,
		new Map([
			[
				TypeFilterValueKey.Deal,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
					subTypes3P: [NounSubtypeKey.Deal],
				},
			],
		]),
	],
	[
		ProductKeys3P.Hubspot,
		new Map([
			[
				TypeFilterValueKey.CustomerOrg,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CUSTOMER_ORG_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Deal,
				{
					entities3P: [
						...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
						...DEFAULT_CLOUD_GRAPH_DEAL_ENTITY_ATI,
					],
					subTypes3P: [NounSubtypeKey.Deal],
				},
			],
		]),
	],
	[
		ProductKeys3P.Dovetail,
		new Map([
			[
				TypeFilterValueKey.Document,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.LaunchDarkly,
		new Map([
			[
				TypeFilterValueKey.WorkItem,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[ProductKeys3P.Salesforce, buildSalesforce3PTypeFilterConfig],
	[
		ProductKeys3P.Sentry,
		new Map([
			[
				TypeFilterValueKey.WorkItem,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.JiraDC,
		new Map([
			[
				TypeFilterValueKey.Project,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_SPACE_ENTITY_ATI,
					subTypes3P: [
						NounSubtypeKey.Software,
						NounSubtypeKey.Business,
						NounSubtypeKey.ServiceDesk,
						NounSubtypeKey.Project,
					],
				},
			],
			[
				TypeFilterValueKey.Issue,
				{
					entities3P: [
						...DEFAULT_CLOUD_GRAPH_WORK_ITEM_ENTITY_ATI,
						...DEFAULT_CLOUD_GRAPH_PROJECT_ENTITY_ATI,
					],
					subTypes3P: [NounSubtypeKey.Issue],
				},
			],
			[
				TypeFilterValueKey.Comment,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_COMMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.Gitbook,
		new Map([
			[
				TypeFilterValueKey.Document,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.AWSS3,
		new Map([
			[
				TypeFilterValueKey.Document,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.Databricks,
		new Map([
			[
				TypeFilterValueKey.Dashboard,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DASHBOARD_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.DataTable,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DATA_TABLE_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.JsmDC,
		new Map([
			[
				TypeFilterValueKey.Project,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_SPACE_ENTITY_ATI,
					subTypes3P: [NounSubtypeKey.ServiceDesk],
				},
			],
			[
				TypeFilterValueKey.Issue,
				{
					entities3P: [
						...DEFAULT_CLOUD_GRAPH_WORK_ITEM_ENTITY_ATI,
						...DEFAULT_CLOUD_GRAPH_PROJECT_ENTITY_ATI,
					],
					subTypes3P: [NounSubtypeKey.Issue],
				},
			],
			[
				TypeFilterValueKey.Comment,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_COMMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.ForgeAdamVitareqDev,
		new Map([
			[
				TypeFilterValueKey.Document,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.WorkItem,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_WORK_ITEM_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.ForgeAdamVitareqProd,
		new Map([
			[
				TypeFilterValueKey.Document,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.WorkItem,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_WORK_ITEM_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.ForgeK15tScrollSitesDev,
		new Map([
			[
				TypeFilterValueKey.Document,
				{
					entities3P: [
						...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
						...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					],
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Space,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_SPACE_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Design,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DESIGN_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Conversation,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CONVERSATION_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Comment,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_COMMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.ForgeK15tScrollSitesProd,
		new Map([
			[
				TypeFilterValueKey.Document,
				{
					entities3P: [
						...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
						...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					],
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Space,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_SPACE_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Design,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DESIGN_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Conversation,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CONVERSATION_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Comment,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_COMMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.ForgePioAwsDev,
		new Map([
			[
				TypeFilterValueKey.Repository,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_REPOSITORY_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.PullRequest,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_PULL_REQUEST_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Branch,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_BRANCH_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Commit,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_COMMIT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Document,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.ForgePioAwsProd,
		new Map([
			[
				TypeFilterValueKey.Repository,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_REPOSITORY_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.PullRequest,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_PULL_REQUEST_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Branch,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_BRANCH_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Commit,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_COMMIT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Document,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.ForgePioClickupDev,
		new Map([
			[
				TypeFilterValueKey.Comment,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_COMMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Space,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_SPACE_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.WorkItem,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_WORK_ITEM_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.ForgePioClickupProd,
		new Map([
			[
				TypeFilterValueKey.Comment,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_COMMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Space,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_SPACE_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.WorkItem,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_WORK_ITEM_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.ForgePioGitbookDev,
		new Map([
			[
				TypeFilterValueKey.Document,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Space,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_SPACE_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.ForgePioGitbookProd,
		new Map([
			[
				TypeFilterValueKey.Document,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Space,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_SPACE_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.ForgePioGithubDev,
		new Map([
			[
				TypeFilterValueKey.Document,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Comment,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_COMMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Commit,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_COMMIT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Branch,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_BRANCH_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Project,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_PROJECT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.WorkItem,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_WORK_ITEM_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.ForgePioGithubProd,
		new Map([
			[
				TypeFilterValueKey.Document,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Comment,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_COMMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Commit,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_COMMIT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Branch,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_BRANCH_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Project,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_PROJECT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.WorkItem,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_WORK_ITEM_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.ForgePioGitlabDev,
		new Map([
			[
				TypeFilterValueKey.Document,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Conversation,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CONVERSATION_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Commit,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_COMMIT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Branch,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_BRANCH_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Project,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_PROJECT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.WorkItem,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_WORK_ITEM_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.ForgePioGitlabProd,
		new Map([
			[
				TypeFilterValueKey.Document,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Conversation,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CONVERSATION_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Commit,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_COMMIT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Branch,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_BRANCH_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Project,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_PROJECT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.WorkItem,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_WORK_ITEM_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.ForgePioGoogleDriveDev,
		new Map([
			[
				TypeFilterValueKey.Video,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_VIDEO_ENTITY_ATI,
					subTypes3P: [NounSubtypeKey.Video],
				},
			],
			[
				TypeFilterValueKey.Document,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.ForgePioGoogleDriveProd,
		new Map([
			[
				TypeFilterValueKey.Video,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_VIDEO_ENTITY_ATI,
					subTypes3P: [NounSubtypeKey.Video],
				},
			],
			[
				TypeFilterValueKey.Document,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.ForgePioGuruDev,
		new Map([
			[
				TypeFilterValueKey.Document,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.WorkItem,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_WORK_ITEM_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.ForgePioGuruProd,
		new Map([
			[
				TypeFilterValueKey.Document,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.WorkItem,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_WORK_ITEM_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.ForgePioHelpjuiceDev,
		new Map([
			[
				TypeFilterValueKey.Document,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.ForgePioHelpjuiceProd,
		new Map([
			[
				TypeFilterValueKey.Document,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.ForgePioHubspotDev,
		new Map([
			[
				TypeFilterValueKey.WorkItem,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_WORK_ITEM_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.CustomerOrg,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CUSTOMER_ORG_ENTITY_ATI,
					subTypes3P: [NounSubtypeKey.CustomerOrg],
				},
			],
			[
				TypeFilterValueKey.Deal,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEAL_ENTITY_ATI,
					subTypes3P: [NounSubtypeKey.Deal],
				},
			],
		]),
	],
	[
		ProductKeys3P.ForgePioHubspotProd,
		new Map([
			[
				TypeFilterValueKey.WorkItem,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_WORK_ITEM_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.CustomerOrg,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CUSTOMER_ORG_ENTITY_ATI,
					subTypes3P: [NounSubtypeKey.CustomerOrg],
				},
			],
			[
				TypeFilterValueKey.Deal,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEAL_ENTITY_ATI,
					subTypes3P: [NounSubtypeKey.Deal],
				},
			],
		]),
	],
	[
		ProductKeys3P.ForgePioSalesforceDev,
		new Map([
			[
				TypeFilterValueKey.WorkItem,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_WORK_ITEM_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.CustomerOrg,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CUSTOMER_ORG_ENTITY_ATI,
					subTypes3P: [NounSubtypeKey.CustomerOrg],
				},
			],
			[
				TypeFilterValueKey.Deal,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEAL_ENTITY_ATI,
					subTypes3P: [NounSubtypeKey.Deal],
				},
			],
		]),
	],
	[
		ProductKeys3P.ForgePioSalesforceProd,
		new Map([
			[
				TypeFilterValueKey.WorkItem,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_WORK_ITEM_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.CustomerOrg,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CUSTOMER_ORG_ENTITY_ATI,
					subTypes3P: [NounSubtypeKey.CustomerOrg],
				},
			],
			[
				TypeFilterValueKey.Deal,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEAL_ENTITY_ATI,
					subTypes3P: [NounSubtypeKey.Deal],
				},
			],
		]),
	],
	[
		ProductKeys3P.ForgePioSharepointDev,
		new Map([
			[
				TypeFilterValueKey.Document,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					subTypes3P: [NounSubtypeKey.Document, NounSubtypeKey.Pdf],
				},
			],
		]),
	],
	[
		ProductKeys3P.ForgePioSharepointProd,
		new Map([
			[
				TypeFilterValueKey.Document,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.ForgePioStackOverflowDev,
		new Map([
			[
				TypeFilterValueKey.Document,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Conversation,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CONVERSATION_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Comment,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_COMMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.ForgePioStackOverflowProd,
		new Map([
			[
				TypeFilterValueKey.Document,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Conversation,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CONVERSATION_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Comment,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_COMMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.ForgeUtoolityGitbookDev,
		new Map([
			[
				TypeFilterValueKey.Document,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.ForgeUtoolityGitbookProd,
		new Map([
			[
				TypeFilterValueKey.Document,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.ForgeGdriveEugeneTestDev,
		new Map([
			[
				TypeFilterValueKey.Document,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.WorkItem,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_WORK_ITEM_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Project,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_PROJECT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Video,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_VIDEO_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Design,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DESIGN_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Message,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_MESSAGE_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Conversation,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CONVERSATION_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Comment,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_COMMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Repository,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_REPOSITORY_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.PullRequest,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_PULL_REQUEST_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Commit,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_COMMIT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Branch,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_BRANCH_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Deal,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEAL_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.CustomerOrg,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CUSTOMER_ORG_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Space,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_SPACE_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Dashboard,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DASHBOARD_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.DataTable,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DATA_TABLE_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.WebPage,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Calendar,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CALENDAR_EVENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.ForgeGdriveEugeneTestProd,
		new Map([
			[
				TypeFilterValueKey.Document,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.WorkItem,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_WORK_ITEM_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Project,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_PROJECT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Video,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_VIDEO_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Design,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DESIGN_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Message,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_MESSAGE_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Conversation,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CONVERSATION_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Comment,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_COMMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Repository,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_REPOSITORY_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.PullRequest,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_PULL_REQUEST_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Commit,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_COMMIT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Branch,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_BRANCH_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Deal,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEAL_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.CustomerOrg,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CUSTOMER_ORG_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Space,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_SPACE_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Dashboard,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DASHBOARD_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.DataTable,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DATA_TABLE_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.WebPage,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Calendar,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CALENDAR_EVENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.ForgeUnitoSalesforceDev,
		new Map([
			[
				TypeFilterValueKey.Document,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.WorkItem,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_WORK_ITEM_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Project,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_PROJECT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Video,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_VIDEO_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Design,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DESIGN_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Message,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_MESSAGE_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Conversation,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CONVERSATION_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Comment,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_COMMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Repository,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_REPOSITORY_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.PullRequest,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_PULL_REQUEST_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Commit,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_COMMIT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Branch,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_BRANCH_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.CustomerOrg,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CUSTOMER_ORG_ENTITY_ATI,
					subTypes3P: [NounSubtypeKey.CustomerOrg],
				},
			],
			[
				TypeFilterValueKey.Deal,
				{
					entities3P: [
						...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
						...DEFAULT_CLOUD_GRAPH_DEAL_ENTITY_ATI,
					],
					subTypes3P: [NounSubtypeKey.Deal],
				},
			],
			[
				TypeFilterValueKey.Space,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_SPACE_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Dashboard,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DASHBOARD_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.DataTable,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DATA_TABLE_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.WebPage,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Calendar,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CALENDAR_EVENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.ForgeUnitoSalesforceProd,
		new Map([
			[
				TypeFilterValueKey.Document,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.WorkItem,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_WORK_ITEM_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Project,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_PROJECT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Video,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_VIDEO_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Design,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DESIGN_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Message,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_MESSAGE_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Conversation,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CONVERSATION_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Comment,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_COMMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Repository,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_REPOSITORY_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.PullRequest,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_PULL_REQUEST_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Commit,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_COMMIT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Branch,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_BRANCH_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.CustomerOrg,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CUSTOMER_ORG_ENTITY_ATI,
					subTypes3P: [NounSubtypeKey.CustomerOrg],
				},
			],
			[
				TypeFilterValueKey.Deal,
				{
					entities3P: [
						...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
						...DEFAULT_CLOUD_GRAPH_DEAL_ENTITY_ATI,
					],
					subTypes3P: [NounSubtypeKey.Deal],
				},
			],
			[
				TypeFilterValueKey.Space,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_SPACE_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Dashboard,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DASHBOARD_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.DataTable,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DATA_TABLE_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.WebPage,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Calendar,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CALENDAR_EVENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.ForgeUnitoServicenowDev,
		new Map([
			[
				TypeFilterValueKey.Document,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.WorkItem,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_WORK_ITEM_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Project,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_PROJECT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Video,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_VIDEO_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Design,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DESIGN_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Message,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_MESSAGE_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Conversation,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CONVERSATION_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Comment,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_COMMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Repository,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_REPOSITORY_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.PullRequest,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_PULL_REQUEST_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Commit,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_COMMIT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Branch,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_BRANCH_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.CustomerOrg,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CUSTOMER_ORG_ENTITY_ATI,
					subTypes3P: [NounSubtypeKey.CustomerOrg],
				},
			],
			[
				TypeFilterValueKey.Deal,
				{
					entities3P: [
						...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
						...DEFAULT_CLOUD_GRAPH_DEAL_ENTITY_ATI,
					],
					subTypes3P: [NounSubtypeKey.Deal],
				},
			],
			[
				TypeFilterValueKey.Space,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_SPACE_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Dashboard,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DASHBOARD_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.DataTable,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DATA_TABLE_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.WebPage,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Calendar,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CALENDAR_EVENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.ForgeUnitoServicenowProd,
		new Map([
			[
				TypeFilterValueKey.Document,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.WorkItem,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_WORK_ITEM_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Project,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_PROJECT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Video,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_VIDEO_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Design,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DESIGN_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Message,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_MESSAGE_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Conversation,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CONVERSATION_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Comment,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_COMMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Repository,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_REPOSITORY_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.PullRequest,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_PULL_REQUEST_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Commit,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_COMMIT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Branch,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_BRANCH_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.CustomerOrg,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CUSTOMER_ORG_ENTITY_ATI,
					subTypes3P: [NounSubtypeKey.CustomerOrg],
				},
			],
			[
				TypeFilterValueKey.Deal,
				{
					entities3P: [
						...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
						...DEFAULT_CLOUD_GRAPH_DEAL_ENTITY_ATI,
					],
					subTypes3P: [NounSubtypeKey.Deal],
				},
			],
			[
				TypeFilterValueKey.Space,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_SPACE_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Dashboard,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DASHBOARD_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.DataTable,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DATA_TABLE_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.WebPage,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Calendar,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CALENDAR_EVENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.ForgeUnitoWrikeDev,
		new Map([
			[
				TypeFilterValueKey.Document,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.WorkItem,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_WORK_ITEM_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Project,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_PROJECT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Video,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_VIDEO_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Design,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DESIGN_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Message,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_MESSAGE_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Conversation,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CONVERSATION_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Comment,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_COMMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Repository,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_REPOSITORY_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.PullRequest,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_PULL_REQUEST_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Commit,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_COMMIT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Branch,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_BRANCH_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.CustomerOrg,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CUSTOMER_ORG_ENTITY_ATI,
					subTypes3P: [NounSubtypeKey.CustomerOrg],
				},
			],
			[
				TypeFilterValueKey.Deal,
				{
					entities3P: [
						...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
						...DEFAULT_CLOUD_GRAPH_DEAL_ENTITY_ATI,
					],
					subTypes3P: [NounSubtypeKey.Deal],
				},
			],
			[
				TypeFilterValueKey.Space,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_SPACE_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Dashboard,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DASHBOARD_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.DataTable,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DATA_TABLE_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.WebPage,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Calendar,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CALENDAR_EVENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.ForgeUnitoWrikeProd,
		new Map([
			[
				TypeFilterValueKey.Document,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.WorkItem,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_WORK_ITEM_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Project,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_PROJECT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Video,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_VIDEO_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Design,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DESIGN_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Message,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_MESSAGE_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Conversation,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CONVERSATION_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Comment,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_COMMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Repository,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_REPOSITORY_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.PullRequest,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_PULL_REQUEST_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Commit,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_COMMIT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Branch,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_BRANCH_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.CustomerOrg,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CUSTOMER_ORG_ENTITY_ATI,
					subTypes3P: [NounSubtypeKey.CustomerOrg],
				},
			],
			[
				TypeFilterValueKey.Deal,
				{
					entities3P: [
						...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
						...DEFAULT_CLOUD_GRAPH_DEAL_ENTITY_ATI,
					],
					subTypes3P: [NounSubtypeKey.Deal],
				},
			],
			[
				TypeFilterValueKey.Space,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_SPACE_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Dashboard,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DASHBOARD_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.DataTable,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DATA_TABLE_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.WebPage,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Calendar,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CALENDAR_EVENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.ForgeUnitoAsanaDev,
		new Map([
			[
				TypeFilterValueKey.Document,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.WorkItem,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_WORK_ITEM_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Project,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_PROJECT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Video,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_VIDEO_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Design,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DESIGN_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Message,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_MESSAGE_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Conversation,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CONVERSATION_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Comment,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_COMMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Repository,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_REPOSITORY_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.PullRequest,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_PULL_REQUEST_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Commit,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_COMMIT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Branch,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_BRANCH_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.CustomerOrg,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CUSTOMER_ORG_ENTITY_ATI,
					subTypes3P: [NounSubtypeKey.CustomerOrg],
				},
			],
			[
				TypeFilterValueKey.Deal,
				{
					entities3P: [
						...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
						...DEFAULT_CLOUD_GRAPH_DEAL_ENTITY_ATI,
					],
					subTypes3P: [NounSubtypeKey.Deal],
				},
			],
			[
				TypeFilterValueKey.Space,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_SPACE_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Dashboard,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DASHBOARD_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.DataTable,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DATA_TABLE_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.WebPage,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Calendar,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CALENDAR_EVENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.ForgeUnitoAsanaProd,
		new Map([
			[
				TypeFilterValueKey.Document,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.WorkItem,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_WORK_ITEM_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Project,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_PROJECT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Video,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_VIDEO_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Design,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DESIGN_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Message,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_MESSAGE_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Conversation,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CONVERSATION_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Comment,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_COMMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Repository,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_REPOSITORY_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.PullRequest,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_PULL_REQUEST_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Commit,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_COMMIT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Branch,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_BRANCH_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.CustomerOrg,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CUSTOMER_ORG_ENTITY_ATI,
					subTypes3P: [NounSubtypeKey.CustomerOrg],
				},
			],
			[
				TypeFilterValueKey.Deal,
				{
					entities3P: [
						...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
						...DEFAULT_CLOUD_GRAPH_DEAL_ENTITY_ATI,
					],
					subTypes3P: [NounSubtypeKey.Deal],
				},
			],
			[
				TypeFilterValueKey.Space,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_SPACE_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Dashboard,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DASHBOARD_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.DataTable,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DATA_TABLE_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.WebPage,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Calendar,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CALENDAR_EVENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.ForgeUnitoBasecampDev,
		new Map([
			[
				TypeFilterValueKey.Document,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.WorkItem,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_WORK_ITEM_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Project,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_PROJECT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Video,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_VIDEO_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Design,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DESIGN_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Message,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_MESSAGE_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Conversation,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CONVERSATION_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Comment,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_COMMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Repository,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_REPOSITORY_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.PullRequest,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_PULL_REQUEST_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Commit,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_COMMIT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Branch,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_BRANCH_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.CustomerOrg,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CUSTOMER_ORG_ENTITY_ATI,
					subTypes3P: [NounSubtypeKey.CustomerOrg],
				},
			],
			[
				TypeFilterValueKey.Deal,
				{
					entities3P: [
						...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
						...DEFAULT_CLOUD_GRAPH_DEAL_ENTITY_ATI,
					],
					subTypes3P: [NounSubtypeKey.Deal],
				},
			],
			[
				TypeFilterValueKey.Space,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_SPACE_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Dashboard,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DASHBOARD_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.DataTable,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DATA_TABLE_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.WebPage,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Calendar,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CALENDAR_EVENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
	[
		ProductKeys3P.ForgeUnitoBasecampProd,
		new Map([
			[
				TypeFilterValueKey.Document,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.WorkItem,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_WORK_ITEM_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Project,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_PROJECT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Video,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_VIDEO_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Design,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DESIGN_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Message,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_MESSAGE_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Conversation,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CONVERSATION_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Comment,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_COMMENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Repository,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_REPOSITORY_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.PullRequest,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_PULL_REQUEST_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Commit,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_COMMIT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Branch,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DEVINFO_BRANCH_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.CustomerOrg,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CUSTOMER_ORG_ENTITY_ATI,
					subTypes3P: [NounSubtypeKey.CustomerOrg],
				},
			],
			[
				TypeFilterValueKey.Deal,
				{
					entities3P: [
						...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
						...DEFAULT_CLOUD_GRAPH_DEAL_ENTITY_ATI,
					],
					subTypes3P: [NounSubtypeKey.Deal],
				},
			],
			[
				TypeFilterValueKey.Space,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_SPACE_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Dashboard,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DASHBOARD_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.DataTable,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_DATA_TABLE_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.WebPage,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
					subTypes3P: [],
				},
			],
			[
				TypeFilterValueKey.Calendar,
				{
					entities3P: DEFAULT_CLOUD_GRAPH_CALENDAR_EVENT_ENTITY_ATI,
					subTypes3P: [],
				},
			],
		]),
	],
]);

/**
 * Resolves a registry entry to a TypeFilterConfig3P.
 * Handles both static Map values and dynamic getter functions.
 */
export const getTypeFilterConfig = (
	entry: TypeFilterConfig3P | (() => TypeFilterConfig3P),
): TypeFilterConfig3P => {
	return typeof entry === 'function' ? entry() : entry;
};
